import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./listingDetailV2.html";
import { selectParams, WireStore } from '@oneappexchange/lwc-wired';
import * as kagekiri from 'kagekiri';
import equalityFn from 'zustand/shallow';
import { getChannel } from '~/modules/analytics/channel';
import { getPricingCtaTitle } from '~/modules/analytics/listingCtaAnalytics';
import { aiAssetEngagement } from '~/modules/analytics/gtmEventTypes/aiAssetAnalyticsContract';
import { GtmListingDetailClickType } from '~/modules/analytics/gtmEventTypes/gtmListingDetailClickType';
import { CLAUDEFORCE_LISTING_ID } from '~/constants';
import { FeatureFlags } from '~/modules/feature_flags';
import { selectRequiredListings } from '~/modules/helpers/compatibilityHelper';
import { isExternalAgentExperienceAvailable } from '~/modules/helpers/externalAgentSearchHelper';
import { getListingLogo, selectProviderAchievementCards } from '~/modules/helpers/listingHelper';
import { isFreeListing, isPricingEligibleForRequestPrivateOffer } from '~/modules/helpers/pricingHelper';
import { generateBaseUrl, generateLegacyListingUrl, login } from '~/modules/helpers/routingHelper';
import { Routes } from '~/modules/routes';
import { isSecurityReviewRequiredListingError } from '~/modules/services/listingService';
import { initialPageRenderedMetricsCheck } from '~/modules/services/metricsService';
import { i18nStore, selectI18n } from '~/modules/stores/i18nStore';
import { listingStore, selectListing } from '~/modules/stores/listingStore';
import { ensureProviderListings, MIN_PROVIDER_LISTINGS_TO_DISPLAY, moreFromProviderStore, MoreFromThisProviderLoadingStatus, V2_PROVIDER_LISTINGS_TO_DISPLAY } from '~/modules/stores/moreFromProviderStore';
import { recentItemStore } from '~/modules/stores/recentItemStore';
import { navigate, routerStore, selectMatch } from '~/modules/stores/routerStore';
import { SavedListingsLoadingStatus, selectUserFavorite, userStore } from '~/modules/stores/userStore';
import { DeviceType, selectDeviceType, viewportStore } from '~/modules/stores/viewportStore';
import { ERROR_CODES, handleException } from '~/modules/utilities/error_utilities';
import { getExternalInstallConfig } from '~/modules/utilities/external_install_utils';
import { renderErrorInPlace } from '~/modules/utilities/errorUtilities';
import { sanitizeText } from '~/modules/utilities/html_utilities';
import { generateListingUrl, isConsultantListing, isListingMatchingId, isListingW9Compliant, isPluginListing, isSlackListing, isSkillListing, isSupportedTechnologyType, isValidListingId, selectExtensionListing, selectListingTryOptions } from '~/modules/utilities/listing_utils';
import { checkReferralParams, checkUtmParams, getUtmParamsFromLocalStorage, isExpired, REFERRAL_CODE_PARAM, storeAnalyticsReferrer, updateLocalStorage } from '~/modules/utilities/localStorageUtilities';
import { logger } from '~/modules/utilities/logger';
import { sendNotification } from '~/modules/utilities/notificationUtilities';
import { selectLeadTrialInformation } from '~/modules/utilities/oaf_schema_utils';
import { getClaudePluginInstallCommands, getPluginFiles, shouldHidePluginSpecs } from '~/modules/utilities/plugin_utils';
import { upsertQueryParams } from '~/modules/utilities/router';
import { getParentPluginListingIds, getRelatedSkillListingIds, getSkillFiles, getSkillSupportedHarnesses, getSkillInstallOptions } from '~/modules/utilities/skill_utils';
const OVERVIEW_TAB_CODE = 'e';
const REVIEWS_TAB_CODE = 'r';
const RESOURCES_TAB_CODE = 's';
const COMPETENCIES_TAB_CODE = 'c';
// v2 tabs: the v1 More Details panel is split into first-class Specs / Security
// / Support tabs, and a dedicated Pricing tab is added.
const SPECS_TAB_CODE = 'x';
const SECURITY_TAB_CODE = 'y';
const PRICING_TAB_CODE = 'p';
const SUPPORT_TAB_CODE = 'u';
// Skill listings only; distinct from PRICING_TAB_CODE ('p' is already taken).
const PACKAGE_FILES_TAB_CODE = 'f';
// Skill listings only, and only when the skill has ≥1 parent plugin.
const FOUND_IN_TAB_CODE = 'o';
const CTA_PARAM = 'cta';
const CLAUDEFORCE_BETA_SIGNUP_SUCCESS_HASH = '#claudeforceBetaSignupSuccess';

// Summary-collapse trigger — the summary header collapses into the compact
// fixed bar (logo · name · tabs) that pins under the sitewide nav. Active on ALL
// breakpoints: on tablet/phone the collapsed bar renders the tab group as the
// Figma "Tab Navigation" dropdown (7909:93145).
//
// Purely geometric and independent of the sitewide nav's own *collapse* behavior:
// we watch only where the inline tab bar sits relative to the nav's live bottom
// edge. Collapse the moment the tab bar has scrolled up out of view behind the
// nav (the active tab's content has reached the top of the page); re-expand once
// it scrolls back down into view. This holds at every width and needs no special
// desktop path — in particular we do NOT wait for the nav to shrink to trigger
// (the old ≥1280 path did), so it keeps working whether or not the nav collapses.
//
// `--ace-header-height` is read as the nav's *current* bottom edge — both for
// this comparison and for positioning the fixed bar. Today's design-system nav
// still republishes a smaller value on scroll (≈144→80); that's fine here because
// the comparison is against the live edge (where the bar actually pins), and by
// the time the tall summary + tab bar have scrolled up far enough to reach the
// nav the nav has long since settled to its simplified height. Its 144→80 height
// change happens near the page top, where the compact bar is still hidden — so
// the change is never visible. If a future nav keeps a fixed height, this same
// code simply reads a constant. See the `.collapsed-nav` padding-top transition
// in the CSS, which stays as belt-and-braces cover for the nav's height motion.
//
// Hysteresis gap for the *hide*: once collapsed, the tab bar must scroll back
// down past the nav by this margin before we re-expand. Prevents a 1px jitter at
// the exact crossover from toggling the bar on/off.
const HEADER_COLLAPSE_HYSTERESIS_PX = 8;
/**
 * Listing Detail v2.
 *
 * This began as a STANDALONE WHOLESALE COPY of the former v1 `pages/listingDetail`
 * (not a subclass). We deliberately did NOT `extend ListingDetail`: v1 had to
 * stay frozen so its retirement at 100% rollout could be a pure delete with no
 * `super()` seam to unwind. v1 `pages/listingDetail` has since been deleted
 * (W-24557579). See `docs/listing-detail-v2.md` and `listing/overviewV2` for the
 * same rationale applied to the Overview panel.
 *
 * The intentional v2 divergences are marked "v2:".
 *
 * v2 divergences:
 *   - owns its own template/CSS (the v2 layout is being fully reimagined).
 *
 * Rendered by `pages/listingDetailWrapper` (the sole listing-detail page).
 *
 * Tradeoff we accept: `git blame` on this file threads back only to the fork
 * commit; use `git blame -C -C` / `git log --follow` to reach the pre-fork
 * history, which also stays reachable via `git log -- <v1 path>` after v1 is
 * deleted.
 */
class ListingDetailV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    // @ts-expect-error * Automated Type Error Fix
    this.i18n = null;
    // @ts-expect-error * Automated Type Error Fix
    this.i18nModule = null;
    this.firstRendered = false;
    this.userAuthenticated = false;
    this.isLoading = false;
    this.userToken = '';
    this.userId = '';
    this.savedListings = [];
    this.savedListingsLoadingStatus = SavedListingsLoadingStatus.UNINITILIAZED;
    this.isListingLoading = true;
    // @ts-expect-error * Automated Type Error Fix
    this.listing = null;
    // @ts-expect-error * Automated Type Error Fix
    this.publisher = null;
    // holding the listing ID so we can easily respond to
    // store updates; this is tied to router params arg
    this.currentListingId = '';
    this.currentListingRouteUrl = '';
    this.listingRequestGeneration = 0;
    /**
     * Prerequisite (required) listing for the Specs tab's Required Listings card.
     * Fetched client-side when the Specs tab becomes active (see fetchPrerequisiteListings).
     */
    this.prerequisiteListing = null;
    this.showOverview = true;
    this.showReviews = false;
    this.showSpecs = false;
    this.showSecurity = false;
    this.showSupport = false;
    this.showPricing = false;
    this.showResources = false;
    this.showCompetencies = false;
    this.showPackageFiles = false;
    this.showFoundIn = false;
    // Install cards announce their effective state for analytics. Keep this one-way so a
    // listing-specific fallback never overwrites the skill sidebar's saved harness preference.
    /**
     * Per-tab-group registration payloads from `tabregister`, keyed by the
     * group's `data-id`. The page mounts two tab groups on desktop — the inline
     * `tab-group` and the collapsed-nav `collapsed-tab-group` — and each fires
     * `tabregister` on first render. Keying by `data-id` (rather than the old
     * single overwrite-wins slot) lets `activeTabRegistration` wire aria/focus to
     * whichever group is currently visible instead of whichever registered last.
     */
    this.tabRegistrations = {};
    /** Tracks the last tab we programmatically focused, so a deep-link focus
     * happens once per tab change and scroll-driven re-renders don't yank focus. */
    this.lastFocusedTab = null;
    /**
     * After a tab pick from the collapsed-nav dropdown, `handleTabChange` scrolls
     * the in-page tab bar back into view, which expands the summary and marks the
     * collapsed group `inert`. Focus was on that group's trigger; Chromium then
     * dumps it to the document root. Re-focus the now-visible in-page control on
     * the expand render (after `syncTabGroupInertness`).
     */
    this.pendingInlineTabFocusAfterExpand = false;
    this.currentTab = null;
    this.offerRequestSuccessMessage = '';
    this.claudeforceBetaSignupSucceeded = false;
    /**
     * True once the compact fixed bar (logo · name · tabs) should show — driven
     * purely by the inline tab bar scrolling up behind the nav's bottom edge (see
     * the trigger comment block above), independent of the sitewide nav's own
     * collapse behavior. Active on every breakpoint.
     */
    this.isSummaryCollapsed = false;
    /** Passive scroll listener that re-evaluates the compact-bar trigger on scroll. */
    this.headerScrollHandler = null;
    /**
     * Passive resize listener that re-evaluates the compact-bar trigger on
     * viewport change. `isSummaryCollapsed` is otherwise only updated on scroll,
     * so a width change with no scroll — which can shift the tab bar's geometry
     * or the nav height — would leave a stale collapsed/expanded bar until the
     * next scroll. Registered next to `headerScrollHandler` and torn down with
     * it.
     */
    this.headerResizeHandler = null;
    /**
     * Pending `requestAnimationFrame` id used to coalesce scroll/resize bursts to
     * one collapse evaluation per painted frame. `null` when no frame is queued.
     * `evaluateSummaryCollapse` reads layout (`getBoundingClientRect`) and computed
     * styles (`getComputedStyle` via `readPxVar`); running it on every raw scroll
     * tick forces a synchronous layout/style recalc per event — up to 120/s on a
     * 120Hz display. Batching through rAF caps it at one read per frame. A queued
     * frame is cancelled in `disconnectedCallback`.
     */
    this.scrollFrameId = null;
    /**
     * True once the summary has collapsed at least once. Gates the render of the
     * inner collapsed-nav tab group (`if:true={hasCollapsedOnce}` in the template)
     * so the common Overview landing — where the user never scrolls far enough to
     * collapse — doesn't pay for a second full tab-group render (+ its `tabregister`
     * a11y wiring and dropdown machinery) on first paint. The `.collapsed-nav`
     * wrapper itself stays mounted so its open/close height animation still runs;
     * only the inner group is deferred. Once true it stays true.
     */
    this.hasCollapsedOnce = false;
    /**
     * Constructors for the three heaviest tab panels — Reviews, Specs, Security —
     * lazily imported the first time their tab activates (see `showActiveTabPanel`)
     * and bound via `lwc:dynamic` in the template. `null` keeps them out of the
     * initial `listingDetailV2` chunk so the common Overview landing doesn't ship
     * their JS/CSS on first paint; each resolves to a separate on-demand sub-chunk.
     */
    this.reviewsPanelCtor = null;
    this.specsPanelCtor = null;
    this.securityPanelCtor = null;
    /**
     * Tallest `--ace-header-height` observed so far, in px (0 until first sampled).
     * The sitewide nav shrinks on scroll-down and re-expands on scroll-up, so its
     * *live* height mid-page is the compact value. Any upward scroll that means to
     * land content just under the nav must reserve for the expanded height, not the
     * compact one it reads at that instant, or the content lands behind the newly
     * taller header. Two such scrolls rely on this: `handleReviewsFiltered` (JS)
     * and the tab-pick scroll (CSS, via `--tab-trigger-scroll-margin` reserving
     * against the published `--ace-header-height-max`). We sample the max from
     * `evaluateSummaryCollapse` (which runs on every scroll frame, catching the
     * full height near the top) and fall back to the live height until an expanded
     * value has been seen.
     */
    this.maxObservedHeaderHeightPx = 0;
    this.deviceType = void 0;
    // Mirrors the store the `listing-more-from-this-provider-v2` leaf reads, so
    // the page can gate the full-bleed "More from {provider}" band on whether
    // the leaf would actually render content. Moved here (from `overviewV2`)
    // because per Figma node 7609-52105 the band is a page-level full-bleed
    // sibling of the two-column body, not part of the indented tab column.
    this.
    // @ts-expect-error * Automated Type Error Fix
    providerState = null;
    this.formatCarouselItem = carouselItem => {
      return {
        pluginType: 'carousel',
        ...carouselItem
      };
    };
  }
  generateTabUrl(tabCode) {
    return `/${Routes.LISTING}?${upsertQueryParams(['tab', tabCode])}`;
  }
  get tabs() {
    const currentTab = this.currentTab || OVERVIEW_TAB_CODE;
    const tabLabels = i18nStore.getState().i18nStaticCache.listingDetails.tabs;
    const tabs = [];
    let order = 1;

    // Skill listings get a fixed, minimal tab set — no Competencies/Specs/Security/
    // Pricing/Resources, all of which are force/consultant concepts that don't apply.
    if (this.isSkillListing) {
      tabs.push({
        id: 'overview-tab',
        code: OVERVIEW_TAB_CODE,
        label: tabLabels.overview,
        active: currentTab === OVERVIEW_TAB_CODE,
        order: order++,
        url: this.generateTabUrl(OVERVIEW_TAB_CODE)
      });
      // Only skills that belong to ≥1 plugin get a Found In tab, in the 2nd slot.
      if (this.hasParentPlugins) {
        tabs.push({
          id: 'found-in-tab',
          code: FOUND_IN_TAB_CODE,
          label: tabLabels.foundIn,
          active: currentTab === FOUND_IN_TAB_CODE,
          order: order++,
          url: this.generateTabUrl(FOUND_IN_TAB_CODE)
        });
      }
      if (this.hasPackageFiles) {
        tabs.push({
          id: 'packageFiles-tab',
          code: PACKAGE_FILES_TAB_CODE,
          label: tabLabels.packageFiles,
          active: currentTab === PACKAGE_FILES_TAB_CODE,
          order: order++,
          url: this.generateTabUrl(PACKAGE_FILES_TAB_CODE)
        });
      }
      tabs.push({
        id: 'support-tab',
        code: SUPPORT_TAB_CODE,
        label: tabLabels.support,
        active: currentTab === SUPPORT_TAB_CODE,
        order: order++,
        url: this.generateTabUrl(SUPPORT_TAB_CODE)
      });
      return tabs;
    }

    // Plugin listings get the same minimal shell as skills (no Competencies/Security/
    // Pricing/Resources). Specs holds the package-contents breakdown; Files reuses the same
    // Package Files browser as skills, since a plugin now carries its own repoFolderUrl/files.
    if (this.isPluginListing) {
      tabs.push({
        id: 'overview-tab',
        code: OVERVIEW_TAB_CODE,
        label: tabLabels.overview,
        active: currentTab === OVERVIEW_TAB_CODE,
        order: order++,
        url: this.generateTabUrl(OVERVIEW_TAB_CODE)
      });
      if (!shouldHidePluginSpecs(this.listing)) {
        tabs.push({
          id: 'specs-tab',
          code: SPECS_TAB_CODE,
          label: tabLabels.specs,
          active: currentTab === SPECS_TAB_CODE,
          order: order++,
          url: this.generateTabUrl(SPECS_TAB_CODE)
        });
      }
      if (this.hasPackageFiles) {
        tabs.push({
          id: 'packageFiles-tab',
          code: PACKAGE_FILES_TAB_CODE,
          label: tabLabels.packageFiles,
          active: currentTab === PACKAGE_FILES_TAB_CODE,
          order: order++,
          url: this.generateTabUrl(PACKAGE_FILES_TAB_CODE)
        });
      }
      tabs.push({
        id: 'support-tab',
        code: SUPPORT_TAB_CODE,
        label: tabLabels.support,
        active: currentTab === SUPPORT_TAB_CODE,
        order: order++,
        url: this.generateTabUrl(SUPPORT_TAB_CODE)
      });
      return tabs;
    }
    tabs.push({
      id: 'overview-tab',
      code: OVERVIEW_TAB_CODE,
      label: tabLabels.overview,
      active: currentTab === OVERVIEW_TAB_CODE,
      order: order++,
      url: this.generateTabUrl(OVERVIEW_TAB_CODE)
    });

    // Add Competencies tab for consultant listings only
    if (this.isConsultantListing) {
      tabs.push({
        id: 'competencies-tab',
        code: COMPETENCIES_TAB_CODE,
        label: tabLabels.competencies,
        active: currentTab === COMPETENCIES_TAB_CODE,
        order: order++,
        url: this.generateTabUrl(COMPETENCIES_TAB_CODE)
      });
    }

    // Specs / Security / Support surface the former More Details sub-panels
    // as first-class tabs; consultant listings don't carry this content.
    if (!this.isConsultantListing) {
      tabs.push({
        id: 'specs-tab',
        code: SPECS_TAB_CODE,
        label: tabLabels.specs,
        active: currentTab === SPECS_TAB_CODE,
        order: order++,
        url: this.generateTabUrl(SPECS_TAB_CODE)
      });
      tabs.push({
        id: 'security-tab',
        code: SECURITY_TAB_CODE,
        label: tabLabels.security,
        active: currentTab === SECURITY_TAB_CODE,
        order: order++,
        url: this.generateTabUrl(SECURITY_TAB_CODE)
      });
    }
    tabs.push({
      id: 'reviews-tab',
      code: REVIEWS_TAB_CODE,
      label: tabLabels.reviews,
      active: currentTab === REVIEWS_TAB_CODE,
      order: order++,
      url: this.generateTabUrl(REVIEWS_TAB_CODE)
    });

    // Add Pricing tab for non-consultant listings only, excluding strictly-free
    // listings (which have no pricing breakdown to show — see showPricingTab).
    if (this.showPricingTab) {
      tabs.push({
        id: 'pricing-tab',
        code: PRICING_TAB_CODE,
        label: tabLabels.pricing,
        active: currentTab === PRICING_TAB_CODE,
        order: order++,
        url: this.generateTabUrl(PRICING_TAB_CODE)
      });
    }
    if (this.hasListingResources) {
      tabs.push({
        id: 'resources-tab',
        code: RESOURCES_TAB_CODE,
        label: tabLabels.resources,
        active: currentTab === RESOURCES_TAB_CODE,
        order: order++,
        url: this.generateTabUrl(RESOURCES_TAB_CODE)
      });
    }
    tabs.push({
      id: 'support-tab',
      code: SUPPORT_TAB_CODE,
      label: tabLabels.support,
      active: currentTab === SUPPORT_TAB_CODE,
      order: order++,
      url: this.generateTabUrl(SUPPORT_TAB_CODE)
    });
    return tabs;
  }
  routerStoreWire(route) {
    const {
      params,
      url
    } = route;
    const isListingDetailRoute = url === Routes.LISTING || url === Routes.CONSULTANT_LISTING;
    if (!isListingDetailRoute || !params?.listingId) {
      if (this.currentListingId || this.currentListingRouteUrl) {
        this.currentListingId = '';
        this.currentListingRouteUrl = '';
        this.listingRequestGeneration++;
      }
      return;
    }
    const listingChanged = params.listingId !== this.currentListingId || url !== this.currentListingRouteUrl;
    if (listingChanged) {
      this.currentListingId = params.listingId;
      this.currentListingRouteUrl = url;
      this.claudeforceBetaSignupSucceeded = false;

      // Note: This is async code that will load a listing from the API (if not in store already).
      // Any code after this line will continue executing while the listing is being fetched and should
      // not rely on this.listing being set yet.
      this.setOrFetchListing(params.listingId);
    }

    // Reset the offer success message on every navigation. The beta success state stays
    // in memory for same-listing tab navigation after its one-time URL hash is removed.
    this.offerRequestSuccessMessage = '';
    if (window.location.hash === CLAUDEFORCE_BETA_SIGNUP_SUCCESS_HASH) {
      this.claudeforceBetaSignupSucceeded = true;
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
    if (params.offerRequestSent === 'true') {
      // Generate success message using listing data (already available)
      const providerName = this.listing?.publisher?.name || '';
      const i18nState = i18nStore.getState();
      this.offerRequestSuccessMessage = i18nState.i18n.requestPrivateOffer.successMessage({
        PROVIDER_NAME: providerName
      });

      // Clean URL immediately (prevent replay on refresh)
      const cleanQueryString = upsertQueryParams({
        remove: ['offerRequestSent']
      });
      window.history.replaceState({}, '', `/${Routes.LISTING}?${cleanQueryString}`);
    }
    this.applyTabFromRouterAfterListingLoad();
    storeAnalyticsReferrer();
    this.handleLeadSourceTracking(params);
  }
  get isListingW9Compliant() {
    return isListingW9Compliant(this.listing);
  }
  handleLeadSourceTracking(params) {
    // We are checking UTM parameters or referral parameters to then save it and if the user decides to initiate the GIN flow when we generate the lead we can also send the user source info
    // Check UTM parameters or referral parameters and update local storage
    if (checkUtmParams(params) || checkReferralParams(params)) {
      // Replace params.listingId with external_id so when we save the UTM params or referral params we save it with the external_id
      params.listingId = this.getListingId();
      updateLocalStorage(params);
    }
  }
  async setOrFetchListing(listingId = '') {
    const routeUrl = routerStore.getState().url;
    const requestGeneration = ++this.listingRequestGeneration;
    try {
      // @ts-expect-error * Automated Type Error Fix
      this.listing = selectListing(listingStore.getState(), listingId);
      // A cached listing is already authoritative, so it does not need the
      // uncached-load tab hold below. This also avoids briefly resetting an
      // already available deep-link panel to the Overview shell.
      this.isListingLoading = !this.listing;
      if (!this.listing) {
        await listingStore.fetchListing(listingId, {
          logErrors: false
        });
        if (!this.isCurrentListingRequest(listingId, routeUrl, requestGeneration)) {
          return;
        }
        // @ts-expect-error * Automated Type Error Fix
        this.listing = selectListing(listingStore.getState(), listingId);
        if (!this.listing) {
          throw new Error();
        }
      }
      if (isSlackListing(this.listing)) {
        handleException(new Error('Slack listing detail is not available'), 'listingDetailV2.setOrFetchListing', {
          debugData: {
            listingId
          },
          level: 'warn'
        });
        await renderErrorInPlace(ERROR_CODES.PAGE_NOT_FOUND, () => this.isCurrentListingRequest(listingId, routeUrl, requestGeneration), {
          suppressLandingLog: true
        });
        return;
      }
      const isExternalAgentListing = isSkillListing(this.listing) || isPluginListing(this.listing);
      if (!isSupportedTechnologyType(this.listing) || isExternalAgentListing && !isExternalAgentExperienceAvailable()) {
        const url = generateLegacyListingUrl(this.listing);
        logger.warn(`Attempt to access unsupported listing with ID '${this.currentListingId}' and Technology '${this.listing.technology}', redirecting to legacy page: ${url}`);
        window.location.assign(url);
        return;
      }
      if (this.listing.public) {
        this.publisher = this.listing.publisher;
        recentItemStore.updateRecentItems('test-user-id', {
          listingId: this.listing.oafId ?? this.listing.external_id ?? '',
          timestamp: Date.now()
        });
      }
      this.isListingLoading = false;
      this.applyTabFromRouterAfterListingLoad();
    } catch (error) {
      if (!this.isCurrentListingRequest(listingId, routeUrl, requestGeneration)) {
        return;
      }
      if (isSecurityReviewRequiredListingError(error)) {
        handleException(error, 'listingDetailV2.setOrFetchListing', {
          debugData: {
            listingId
          },
          level: 'warn'
        });
        await renderErrorInPlace(ERROR_CODES.SECURITY_REVIEW_REQUIRED, () => this.isCurrentListingRequest(listingId, routeUrl, requestGeneration));
        return;
      }
      if (isSlackListing(this.listing)) {
        const url = window.location.href;
        handleException(error, 'listingDetailV2.setOrFetchListing', {
          debugData: {
            listingId,
            url
          }
        });
        window.location.assign(url);
        return;
      }
      const url = generateLegacyListingUrl(this.listing);
      handleException(error, 'listingDetailV2.setOrFetchListing', {
        debugData: {
          listingId,
          url
        }
      });
      window.location.assign(url);
    }
  }
  isCurrentListingRequest(listingId, routeUrl, requestGeneration) {
    const routeState = routerStore.getState();
    return this.isConnected && this.listingRequestGeneration === requestGeneration && this.currentListingId === listingId && this.currentListingRouteUrl === routeUrl && routeState.url === routeUrl && selectParams(routeState)?.listingId === listingId;
  }

  /**
   * Fetches the first prerequisite (required) listing so the Specs tab can render its
   * Required Listings card. Fire-and-forget, triggered by Specs-tab activation (see
   * `showActiveTabPanel`) rather than on every listing load; failures are swallowed
   * (the card simply doesn't render).
   */
  async fetchPrerequisiteListings() {
    // Snapshot the listing this fetch is for, so a late resolve after the user
    // navigates away doesn't clobber `prerequisiteListing` for the wrong listing.
    const requestedListingId = this.currentListingId;
    // Clear any prior listing's prerequisite up front so a pending or failed
    // lookup can't leave a stale card rendered under the new listing.
    this.prerequisiteListing = null;
    const requiredListingIds = selectRequiredListings(this.extensionListing);
    if (!requiredListingIds || requiredListingIds.length === 0) {
      return;
    }
    const listingId = requiredListingIds[0];
    if (!isValidListingId(listingId)) {
      return;
    }
    try {
      let listing = selectListing(listingStore.getState(), listingId);
      if (!listing) {
        await listingStore.fetchListing(listingId, {
          logErrors: false
        });
        listing = selectListing(listingStore.getState(), listingId);
      }
      if (listing && this.currentListingId === requestedListingId) {
        this.prerequisiteListing = listing;
      }
    } catch (error) {
      handleException(error, 'listingDetail.fetchPrerequisiteListings', {
        level: 'warn',
        debugData: {
          listingId
        }
      });
    }
  }

  /**
   * Fetch the "More from {provider}" carousel data. This page drives the fetch
   * from the Overview tab (the only tab whose `showMoreFromProvider` band consumes
   * it), so a deep link to any other tab never pays the request;
   * `moreFromProviderStore`'s subscription no longer fires the fetch itself.
   * Mirrors the store's `checkListingId` derivation and dedupe so repeated
   * Overview activations don't re-fetch, and requests the larger v2 carousel set
   * (`V2_PROVIDER_LISTINGS_TO_DISPLAY`).
   */
  fetchMoreFromProvider() {
    // This page drives the fetch on Overview activation; the store's subscription
    // no longer fires it. The shared gate (external-agent skip, id/marketplace
    // derivation, and same-listing dedupe) lives in the store.
    ensureProviderListings(this.listing, V2_PROVIDER_LISTINGS_TO_DISPLAY);
  }
  userStoreWire(userState) {
    if (userState) {
      this.userToken = userState.userToken;
      this.userId = userState.userId || '';
      // @ts-expect-error * Automated Type Error Fix
      this.savedListings = userState.savedListings;
      this.userAuthenticated = !userState.isGuestUser;
      this.isLoading = userState.isLoading;
      this.savedListingsLoadingStatus = userState.savedListingsLoadingStatus;
    }
  }
  i18nStoreWire({
    i18n,
    i18nStaticCache
  }) {
    this.i18n = i18nStaticCache;
    this.i18nModule = i18n;
    this.sharedLabels = i18nStaticCache.shared;
    // Note: Tab labels are now handled in the tabs getter
  }

  /**
   * True when `listing-more-from-this-provider-v2` will actually render content
   * (same condition as the leaf's own `displayMoreFromThisProvider`). Gates the
   * page-level band so it doesn't outdent an empty grey strip.
   */
  get hasMoreFromThisProvider() {
    return (this.providerState?.data?.listings?.length ?? 0) >= MIN_PROVIDER_LISTINGS_TO_DISPLAY && this.providerState?.loadingStatus === MoreFromThisProviderLoadingStatus.LOADED;
  }
  get publisherName() {
    return this.publisher?.name ?? '';
  }

  /** Recommendation band scoped to the Overview tab; hidden on Specs/Security/Reviews/etc. */
  get showMoreFromProvider() {
    // Skills and plugins use curated relationships. Do not fall back to the publisher's
    // general inventory when either external-agent listing type has no related metadata.
    return this.showOverview && !this.isSkillListing && !this.isPluginListing && this.hasMoreFromThisProvider;
  }

  /** Skill-only replacement for the standard recommendation band, shown below every skill tab. */
  get showRelatedSkills() {
    return this.isSkillListing && getRelatedSkillListingIds(this.listing).length > 0;
  }
  get hasTrialOptions() {
    return selectListingTryOptions(this.listing).length > 0;
  }
  get tryButtonHref() {
    return `/${Routes.TRIALS}?listingId=${this.getListingId()}`;
  }
  get showRequestPrivateOfferButton() {
    return this.hasPrivateOfferRequestLeadsEnabled && this.isListingW9Compliant && isPricingEligibleForRequestPrivateOffer(this.pricingPlan);
  }
  get isConsultantListing() {
    return isConsultantListing(this.listing);
  }
  get isSkillListing() {
    return isSkillListing(this.listing);
  }
  get isPluginListing() {
    return isPluginListing(this.listing);
  }
  get isClaudeforceListing() {
    return this.listing?.external_id === CLAUDEFORCE_LISTING_ID;
  }

  /** The beta install variant requires both the configured listing and a usable external CTA. */
  get showClaudeforceBetaAccess() {
    if (!FeatureFlags.FF_CLAUDEFORCE_BETA_LEAD_CAPTURE || !this.isClaudeforceListing) {
      return false;
    }
    const externalInstallConfig = getExternalInstallConfig(this.listing);
    return externalInstallConfig.isExternal && !!externalInstallConfig.installUrl;
  }

  /**
   * Skill/plugin logos are self-contained transparent PNGs, so drop the
   * white backing (`ax-image`'s default). App/consultant logos may be
   * white/near-white and rely on that backing for legibility on the
   * light-blue header band, so they keep it.
   */
  get isLooseLogo() {
    return this.isSkillListing || this.isPluginListing;
  }
  get summaryLogoClass() {
    return this.isLooseLogo ? 'lwc-cmp image listing-logo--loose' : 'lwc-cmp image';
  }
  get collapsedLogoClass() {
    return this.isLooseLogo ? 'lwc-cmp collapsed-logo collapsed-logo--loose' : 'lwc-cmp collapsed-logo';
  }

  /** Whether this skill belongs to ≥1 plugin — gates the Found In tab. */
  get hasParentPlugins() {
    return getParentPluginListingIds(this.listing).length > 0;
  }

  /** Files is meaningful only when the current Skill/Plugin publishes at least one usable path. */
  get hasPackageFiles() {
    if (this.isSkillListing) {
      return getSkillFiles(this.listing).length > 0;
    }
    if (this.isPluginListing) {
      return getPluginFiles(this.listing).length > 0;
    }
    return false;
  }

  /** Beta tag next to the title: skills and plugins both ship as beta. */
  get shouldShowBetaBadge() {
    return this.isSkillListing || this.isPluginListing;
  }
  get showLearnMoreButton() {
    return isConsultantListing(this.listing) && !!this.generateCtaUrlLegacySite('lm');
  }
  get showListingPrice() {
    return !isConsultantListing(this.listing) && !this.isSkillListing && !this.isPluginListing;
  }

  /**
   * The Pricing tab. Shown for listings with a price to break down, but NOT for
   * strictly-free listings (`price_model_type === 'free'`): those have only a
   * flat "Free" price and no plans/custom details, so there is nothing to show
   * on a dedicated tab. Freemium / free-to-install keep the tab because they can
   * carry plan tables and limitations. The buy-box "Pricing Details" link is
   * likewise hidden for free listings (see pricingSummaryV2's showPricingDetailsLink).
   */
  get showPricingTab() {
    return this.showListingPrice && !isFreeListing(this.pricingPlan);
  }
  get showLeadCtas() {
    // skills/plugins have no get-it-now/trial/private-offer/chat CTAs
    return !this.isSkillListing && !this.isPluginListing;
  }
  get showSkillOverview() {
    // Skills and plugins render their baked-in SKILL.md / README.md here, along with the shared
    // editorial description and domain categories, instead of product-focused Overview sections.
    return this.showOverview && (this.isSkillListing || this.isPluginListing);
  }
  get showStandardOverview() {
    return this.showOverview && !this.isSkillListing && !this.isPluginListing;
  }
  get showClaudeforceBetaSignupSuccess() {
    return this.isClaudeforceListing && this.claudeforceBetaSignupSucceeded;
  }
  get claudeforceBetaSignupSuccessTitle() {
    return i18nStore.getState().i18nStaticCache.claudeforceBetaAccess.signupSuccessTitle;
  }
  get claudeforceBetaSignupSuccessMessage() {
    return i18nStore.getState().i18nStaticCache.claudeforceBetaAccess.signupSuccessMessage;
  }

  /** Side panel's Price & CTA card: skills and plugins have no pricing/CTAs to show it for. */
  get showPriceCtaCard() {
    return !this.isSkillListing && !this.isPluginListing;
  }
  get showInstallCard() {
    if (!this.isSkillListing && !this.isPluginListing) {
      return false;
    }

    // PluginSidebar deliberately renders a title-only card when neither its external CTA nor
    // its SF Skills native flow has usable data. Keep that explicit fallback visible.
    if (this.isPluginListing) {
      return true;
    }
    const externalInstallConfig = getExternalInstallConfig(this.listing);
    return !externalInstallConfig.isExternal || !!externalInstallConfig.installUrl;
  }

  /** Specs tab body for force/consultant listings — the modernized listing-specs panel. */
  get showForceSpecs() {
    return this.showSpecs && !this.isPluginListing;
  }

  /** Specs tab body for plugin listings — the package-contents breakdown of member skills. */
  get showPluginSpecs() {
    return this.showSpecs && this.isPluginListing;
  }

  // ELEMENT REFERENCES
  get isMobilePhone() {
    return this.deviceType === DeviceType.PHONE;
  }
  get isTabletDevice() {
    return this.deviceType === DeviceType.TABLET;
  }
  get isMobileOrTablet() {
    return this.deviceType === DeviceType.PHONE || this.deviceType === DeviceType.TABLET;
  }

  /**
   * `data-id` of the tab group the user actually sees and interacts with. The
   * collapsed-nav bar is always mounted (on every breakpoint); once the summary
   * collapses it takes over the pinned tab role and the inline bar has scrolled
   * away, so the collapsed group becomes the live one. Before that the inline
   * bar is live. aria wiring, focus, and keyboard nav all target this group so
   * they never land on the off-screen copy.
   */
  get visibleTabGroupId() {
    return this.isSummaryCollapsed ? ListingDetailV2.COLLAPSED_TAB_GROUP_ID : ListingDetailV2.PRIMARY_TAB_GROUP_ID;
  }

  /** Registration payload for the currently-visible tab group (or null if it
   * hasn't registered yet). */
  get activeTabRegistration() {
    return this.tabRegistrations[this.visibleTabGroupId] ?? null;
  }
  get activeTabGroupRef() {
    return kagekiri.querySelector(`[data-id="${this.visibleTabGroupId}"] a[aria-selected="true"]`);
  }
  get logoAlt() {
    return this.listing.name ? this.i18nModule.shared.logoAlt({
      LOGO_NAME: this.listing.name
    }) : this.sharedLabels.deafultLogoAlt;
  }
  renderedCallback() {
    /* Keep exactly one tab group in the tab order + a11y tree. The other
     * group is still mounted (the collapsed bar animates; the inline bar just
     * scrolls away) but marked `inert`, so a screen reader / keyboard user
     * only ever reaches the group that's actually on screen — fixing the case
     * where, once collapsed, focus and SR navigation still landed on the
     * scrolled-away inline tabs instead of the visible collapsed-bar tabs. */
    this.syncTabGroupInertness();

    /* Set accessibility attributes — wire panels to the visible group's tab ids. */
    const registration = this.activeTabRegistration;
    if (registration) {
      this.setTabAttributes(registration);
    }
    const {
      params
    } = routerStore.getState();
    // Move focus to the active tab only when the tab actually changed (deep
    // link / tab switch), not on every scroll-driven re-render — otherwise a
    // collapse toggle would yank focus back to the tab bar mid-scroll, and it
    // could land in the off-screen (inert) group.
    if (this.pendingInlineTabFocusAfterExpand && !this.isSummaryCollapsed) {
      this.pendingInlineTabFocusAfterExpand = false;
      this.lastFocusedTab = this.currentTab;
      // The collapsed-nav pick scroll (`handleTabChange` →
      // `scrollTriggerIntoView`) has already positioned the page under the
      // nav. Refocus without scrolling so the browser's focus-into-view on
      // the scroll-margin-less active `<a>` can't yank the page to the top.
      registration?.focusActiveTab({
        preventScroll: true
      });
    } else if (this.currentTab && registration && params?.tab && this.currentTab !== this.lastFocusedTab) {
      this.lastFocusedTab = this.currentTab;
      registration.focusActiveTab();
    }
    if (!this.firstRendered) {
      this.firstRendered = true;
      initialPageRenderedMetricsCheck();
    }
    // for Agentforce button //
    if (this.displayChat) {
      const chatButton = document.body.querySelector('#agentforceChatButton');
      if (chatButton) {
        chatButton.style.right = '5rem';
      }
    }
    this.setupHeaderCollapseWatch();
  }

  /**
   * Drive the compact fixed bar (see the trigger comment block above) across all
   * breakpoints. A single passive `scroll` listener re-evaluates on scroll; we
   * seed once on setup to cover a deep-link that renders already scrolled.
   */
  setupHeaderCollapseWatch() {
    if (this.headerScrollHandler || typeof window === 'undefined') {
      return;
    }
    this.evaluateSummaryCollapse();
    // Coalesce scroll/resize bursts to one layout-reading evaluation per
    // painted frame (see `scrollFrameId`) instead of one per raw event.
    this.headerScrollHandler = () => this.scheduleSummaryCollapse();
    window.addEventListener('scroll', this.headerScrollHandler, {
      passive: true
    });
    // Also re-evaluate on resize: a width change with no scroll shifts the tab
    // bar's geometry (and can change the nav height), so it can otherwise
    // strand a stale collapsed/expanded bar (see `headerResizeHandler`).
    this.headerResizeHandler = () => this.scheduleSummaryCollapse();
    window.addEventListener('resize', this.headerResizeHandler, {
      passive: true
    });
  }

  /**
   * rAF-throttle the collapse evaluation: while a frame is already queued,
   * further scroll/resize events collapse into the pending one so
   * `evaluateSummaryCollapse` runs at most once per painted frame. Falls back to
   * a direct call where `requestAnimationFrame` is unavailable (SSR/jsdom).
   */
  scheduleSummaryCollapse() {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      this.runCollapseFrame();
      return;
    }
    if (this.scrollFrameId !== null) {
      return;
    }
    this.scrollFrameId = window.requestAnimationFrame(() => {
      this.scrollFrameId = null;
      this.runCollapseFrame();
    });
  }

  /**
   * One coalesced scroll/resize frame: re-evaluate the collapse trigger (cheap —
   * reads cached geometry). Batched through `requestAnimationFrame` so a burst of
   * scroll/resize events runs this at most once per painted frame.
   */
  runCollapseFrame() {
    this.evaluateSummaryCollapse();
  }

  /**
   * Toggle the compact bar purely on the inline tab bar's geometry relative to
   * the nav's live bottom edge — see the trigger comment block above. The
   * *trigger* has no dependency on the sitewide nav collapsing.
   */
  evaluateSummaryCollapse() {
    // `--ace-header-height` is the nav's current height = the y of its bottom
    // edge, which is where the fixed compact bar pins and the point the tab
    // bar must clear to be considered "scrolled behind the nav".
    const navHeight = this.readPxVar('--ace-header-height');
    if (navHeight <= 0) {
      // Nav height not published yet (e.g. first evaluation before layout):
      // there's no valid bottom edge to compare against, so defer. Guards
      // against a spurious collapse when both this and the tab-bar rect read
      // 0 before first paint.
      return;
    }
    // Track the tallest (expanded) header height for the two upward scrolls
    // that re-expand the nav as they run: the reviews-filter recovery scroll
    // (JS, `handleReviewsFiltered`) and the tab-pick scroll (CSS, via
    // `--tab-trigger-scroll-margin`). Publish the max as a body var so that
    // CSS reserve can use a stable expanded height instead of the live one.
    if (navHeight > this.maxObservedHeaderHeightPx) {
      this.maxObservedHeaderHeightPx = navHeight;
      document.body.style.setProperty('--ace-header-height-max', `${navHeight}px`);
    }
    const tabBar = this.template?.querySelector('.tab-bar');
    if (!tabBar) {
      return;
    }
    const tabBarBottom = tabBar.getBoundingClientRect().bottom;
    if (this.isSummaryCollapsed) {
      // Re-expand once the inline tab bar has scrolled back down into view
      // below the nav (past the hysteresis margin, to avoid crossover jitter).
      if (tabBarBottom > navHeight + HEADER_COLLAPSE_HYSTERESIS_PX) {
        this.setSummaryCollapsed(false);
      }
      return;
    }

    // Collapse once the tab bar has scrolled up out of view behind the nav
    // (the active tab's content has reached the top of the page).
    if (tabBarBottom <= navHeight) {
      this.setSummaryCollapsed(true);
    }
  }
  setSummaryCollapsed(nextCollapsed) {
    if (nextCollapsed === this.isSummaryCollapsed) {
      return;
    }
    // First collapse mounts the inner collapsed-nav tab group (see
    // `hasCollapsedOnce` / the template). Once true it stays true, so the
    // group is available on this and every subsequent collapse.
    if (nextCollapsed) {
      this.hasCollapsedOnce = true;
    }
    this.isSummaryCollapsed = nextCollapsed;
  }

  /**
   * The Reviews tab fires `reviewsfiltered` when a filter/sort settles. If it
   * shrank the result set, the reviews column got shorter but its container stays
   * tall (floored to the side panel's height so the sticky filters card keeps its
   * travel), so the document shrinks under the viewport and the user is left
   * scrolled down past the (now shorter) results — a screen of blank floored
   * space, whether the filter emptied the list or just cut 20 reviews to 1.
   * Scroll the results back up — but never far enough to re-expand the collapsed
   * nav.
   *
   * The compact bar hides once the inline `.tab-bar` scrolls back down past the
   * nav (see `evaluateSummaryCollapse`'s hide path). So the highest we may scroll
   * is the point where the tab bar's bottom sits exactly at that boundary: any
   * higher and the bar re-enters view and the header pops back open. We compute
   * the delta from the tab bar's current position relative to that boundary and
   * scroll by it — clamped to upward-only, so when the page isn't scrolled past
   * the boundary (nothing to reclaim — e.g. a filter that grew the list, or one
   * applied while near the top) we do nothing.
   */
  handleReviewsFiltered() {
    if (typeof window === 'undefined') {
      return;
    }
    const tabBar = this.template?.querySelector('.tab-bar');
    if (!tabBar) {
      return;
    }
    // The boundary the hide path uses: full nav height (+ hysteresis so we
    // land just shy of the re-expand threshold, not on it). Use the tallest
    // header height observed rather than the live (compact) one: this upward
    // scroll re-expands the nav, so reserving against the compact height would
    // land the tab bar — and the recovered review content — behind the taller
    // expanded header. Live height is the fallback until an expanded value is
    // sampled (e.g. a deep link that never scrolled through the top).
    const liveNavHeight = this.readPxVar('--ace-header-height');
    const referenceHeight = Math.max(this.maxObservedHeaderHeightPx, liveNavHeight);
    const boundary = referenceHeight + HEADER_COLLAPSE_HYSTERESIS_PX;
    // How far the tab bar sits above the boundary right now. Positive means
    // there's slack to scroll up into; <=0 means the tab bar is already at or
    // below the boundary, so scrolling up would cross it — leave it alone.
    const slack = boundary - tabBar.getBoundingClientRect().bottom;
    if (slack <= 0) {
      return;
    }
    window.scrollBy({
      top: -slack,
      left: 0,
      behavior: 'smooth'
    });
  }
  readPxVar(name) {
    const raw = getComputedStyle(document.body).getPropertyValue(name).trim();
    if (!raw) {
      return 0;
    }
    const parsed = parseFloat(raw);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return raw.endsWith('rem') ? parsed * parseFloat(getComputedStyle(document.documentElement).fontSize || '16') : parsed;
  }
  disconnectedCallback() {
    // Drop the body-level var so a later page doesn't inherit a stale
    // expanded-nav height from this (now-unmounted) listing page.
    if (typeof document !== 'undefined') {
      document.body.style.removeProperty('--ace-header-height-max');
    }
    // Re-arm the max sampler for a later remount so it republishes
    // `--ace-header-height-max` (removed just above) on the next scroll frame.
    this.maxObservedHeaderHeightPx = 0;
    if (this.headerScrollHandler) {
      window.removeEventListener('scroll', this.headerScrollHandler);
      this.headerScrollHandler = null;
    }
    if (this.headerResizeHandler) {
      window.removeEventListener('resize', this.headerResizeHandler);
      this.headerResizeHandler = null;
    }
    if (this.scrollFrameId !== null) {
      window.cancelAnimationFrame(this.scrollFrameId);
      this.scrollFrameId = null;
    }
  }
  get summaryClass() {
    const classes = ['summary'];
    if (this.isSummaryCollapsed) {
      classes.push('summary--collapsed');
    }
    // No blurb → collapse the header to a single row so the title centers
    // against the logo instead of being lifted by an empty description row.
    if (!this.hasSearchBlurb) {
      classes.push('summary--no-blurb');
    }
    if (this.isClaudeforceListing) {
      classes.push('summary--claudeforce');
    }
    return classes.join(' ');
  }

  /**
   * The collapsed bar's tab group runs in `flush` mode ONLY on desktop — there
   * it's an inline tab list welded into the compact 64px row's single divider.
   * On tablet/phone it renders as the Figma "Tab Navigation" dropdown (7909:93145),
   * and `flush` would suppress that dropdown (`tabGroupV2.showMobileDropdown`
   * requires `!flush`), so drop it there.
   */
  get collapsedTabGroupFlush() {
    return !this.isMobileOrTablet;
  }
  get collapsedNavClass() {
    return this.isSummaryCollapsed ? 'collapsed-nav collapsed-nav--open' : 'collapsed-nav';
  }
  get collapsedNavAriaHidden() {
    return !this.isSummaryCollapsed;
  }

  /**
   * Call to Actions methods included here: Get it Now & Watch a demo
   */
  handleGetItNowCTA() {
    this.redirectCallToActionToLegacySite('gin');
  }
  handleLearnMoreCTA() {
    this.redirectCallToActionToLegacySite('lm');
  }
  handleRequestPrivateOfferClick(event) {
    event.preventDefault();
    // Analytics fires automatically from wds-button
    // Add delay to ensure analytics completes before navigation
    setTimeout(() => {
      // Use the current URL's listing ID to maintain consistency throughout the flow
      const listingId = this.currentListingId || this.getListingId();
      navigate(`/${Routes.OFFER_REQUEST}?listingId=${listingId}`);
    }, 300);
  }

  /**
   * Activates the Pricing tab AND deep-links to its details heading. The
   * `scrollTo=pricing-details` param drives `listing-pricing-v2` to scroll that
   * heading into view and move focus onto it (see its renderedCallback) so the
   * section is announced to assistive tech; without it the link only switched
   * tabs and never reached the pricing details. The tab button itself is focused
   * by this page's renderedCallback focusActiveTab call.
   */
  handlePricingSummaryLinkClick() {
    navigate(`/${Routes.LISTING}?${upsertQueryParams(['tab', PRICING_TAB_CODE], ['scrollTo', 'pricing-details'])}`);
  }
  get listingInUserFavorites() {
    return this.userId != null && this.listing &&
    // The listingId that we store in the userAccount comes directly from the url.
    // As a result we dont know which id it corresponds to, so we must check all the Ids.
    this.savedListings?.some(listingId => isListingMatchingId(this.listing, listingId));
  }
  async handleFavoriteListings(event) {
    event.preventDefault();
    if (!this.shouldShowFavoriteButton) {
      return;
    }
    if (!this.userAuthenticated) {
      login();
      return;
    }
    const action = this.listingInUserFavorites ? userStore.removeFavorite : userStore.addFavorite;
    const response = await action(this.currentListingId);
    const previousFocusedElement = this.favoriteButtonRef;
    if (response.isSuccess) {
      const {
        successMessage
      } = response;
      // @ts-expect-error * Automated Type Error Fix
      sendNotification(successMessage, {
        previousFocusedElement
      });
    } else {
      const {
        errorMessage,
        exceptionInfo
      } = response;
      // @ts-expect-error * Automated Type Error Fix
      sendNotification(errorMessage, {
        exceptionInfo,
        previousFocusedElement
      });
    }
  }
  get favoriteButtonRef() {
    const iconIteractive = this.template.querySelector('ax-icon-interactive');
    return kagekiri.querySelector('ax-button', iconIteractive);
  }
  redirectCallToActionToLegacySite(cta) {
    const url = this.generateCtaUrlLegacySite(cta);
    window.location.href = url;
  }
  generateCtaUrlLegacySite(cta) {
    const payloadString = localStorage.getItem(this.getListingId());
    const payload = payloadString ? JSON.parse(payloadString) : null;
    if (payload) {
      const utmData = payload.UTM;
      const sourceData = payload.source;
      const utmExpired = utmData && isExpired(utmData.expirationDate);
      const sourceExpired = sourceData && isExpired(sourceData.expirationDate);
      if (utmData && !utmExpired && sourceData && !sourceExpired) {
        const utmParams = getUtmParamsFromLocalStorage(this.getListingId());
        return this.generateCtaUrlWithUtmAndReferralCode(cta, utmParams, sourceData.referral_code);
      }
      if (utmData && !utmExpired) {
        const utmParams = getUtmParamsFromLocalStorage(this.getListingId());
        return this.generateCtaUrlWithUtm(cta, utmParams);
      }

      // Check if referral_code is present and not expired
      if (sourceData && !sourceExpired) {
        return this.generateLegacySiteUrlWithReferralCode(cta, sourceData.referral_code);
      }
    }

    // If both UTM and referral_code are expired or not present, generate URL with channel
    return this.generateCtaUrl(cta);
  }
  getListingId() {
    return this.listing.external_id ?? (this.listing.appExchangeId || '');
  }
  sanitizeUtmParams(utmParams) {
    if (!utmParams) {
      return null;
    }
    return Object.fromEntries(Object.entries(utmParams).map(([key, value]) => [sanitizeText(key), sanitizeText(value)]));
  }
  generateUrlWithParams(queryParams) {
    const baseUrl = generateBaseUrl();
    const siteUrl = generateListingUrl(baseUrl, this.listing, upsertQueryParams(queryParams));
    return siteUrl;
  }
  generateCtaUrlWithUtm(cta, utmParams) {
    const sanitizedUtmParams = this.sanitizeUtmParams(utmParams);
    const queryParams = {
      insert: [[CTA_PARAM, cta], ...(sanitizedUtmParams ? Object.entries(sanitizedUtmParams) : [])],
      remove: ['referral', 'ref']
    };
    return this.generateUrlWithParams(queryParams);
  }
  generateLegacySiteUrlWithReferralCode(cta, referral_code) {
    const queryParams = {
      insert: [[CTA_PARAM, cta], [REFERRAL_CODE_PARAM, referral_code]],
      remove: ['referral', 'ref']
    };
    return this.generateUrlWithParams(queryParams);
  }
  generateCtaUrlWithUtmAndReferralCode(cta, utmParams, referral_code) {
    const sanitizedUtmParams = this.sanitizeUtmParams(utmParams);
    const queryParams = {
      insert: [[CTA_PARAM, cta], [REFERRAL_CODE_PARAM, referral_code], ...(sanitizedUtmParams ? Object.entries(sanitizedUtmParams) : [])],
      remove: ['referral', 'ref']
    };
    return this.generateUrlWithParams(queryParams);
  }
  generateCtaUrl(cta) {
    const currentUrl = sanitizeText(window.location.href);
    const documentReferrer = sanitizeText(document.referrer);
    const otherSourceInfo = getChannel(currentUrl, documentReferrer, false, false, false);
    const queryParams = {
      insert: [[CTA_PARAM, cta], ['other_source', otherSourceInfo.channel]]
    };
    return this.generateUrlWithParams(queryParams);
  }
  handlePricingCtaAnalytics(event) {
    const name = event.detail.button?.callToAction?.name || event.detail.link?.callToAction?.name;
    if (event.detail.eventType !== 'click' || !['get it now', 'try it free', 'pricing details', 'request private offer'].includes(name)) {
      return;
    }
    event.detail.listingDetailCta = {
      elementTitle: getPricingCtaTitle(this.pricingPlan),
      // Classify the CTA by its purpose; Try It renders an anchor for navigation.
      elementType: name === 'pricing details' ? 'link text' : 'button'
    };
  }

  // @ts-expect-error * Automated Type Error Fix
  handleActionsAnalytics(event) {
    const buttonName = event.detail.button?.callToAction?.name;
    if (buttonName === 'get-it-now-btn') {
      event.detail.clickUrl = this.generateCtaUrlLegacySite('gin');
    }
    if (buttonName === 'learn more') {
      event.detail.clickUrl = this.generateCtaUrlLegacySite('lm');
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleTabGroupKeyDown(event) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      this.activeTabGroupRef?.focus();
    }
  }
  handleTabChange() {
    const tabGroup = this.template?.querySelector('[data-id="tab-group"]');
    const tabBar = this.template?.querySelector('.tab-bar');
    const scrollOptions = {
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth',
      block: 'start',
      inline: 'nearest'
    };
    // Always scroll the in-page tab bar (not the fixed collapsed group) so a
    // pick from the bottom of a long panel, or from the collapsed drawer,
    // lands on the new tab's start. `.tab-column`'s viewport-tall reserve
    // guarantees the bar can always reach under the nav (even for a short
    // panel), so the scroll lands consistently without deferring past the
    // panel swap. Fall back to `.tab-bar` if the API is missing.
    //
    // Do NOT gate this scroll on `!isMobileOrTablet`: a PR review suggested
    // scoping it to desktop (to match the `.tab-column` reserve, which IS
    // desktop-only), but on-device testing showed that broke the scroll-to-top
    // on tablet/phone — there the tab is picked from the collapsed dropdown
    // while scrolled down, and `scrollTriggerIntoView`'s upward reveal is
    // exactly what snaps the new tab's content to the top under the nav. The
    // guard was reverted; scroll-to-top must work on ALL breakpoints. (The
    // reserve is still desktop-only — it isn't load-bearing for this upward
    // `window.scrollBy` scroll, which needs no content below to land.)
    //
    // A pick from the collapsed drawer will expand the summary; refocus the
    // in-page trigger once that group is no longer inert.
    if (this.isSummaryCollapsed) {
      this.pendingInlineTabFocusAfterExpand = true;
    }
    if (typeof tabGroup?.scrollTriggerIntoView === 'function') {
      tabGroup.scrollTriggerIntoView(scrollOptions);
      return;
    }
    tabBar?.scrollIntoView(scrollOptions);
  }
  /** End methods for CTA */

  /**
   * Wire each panel's `aria-labelledby` to the given group's tab anchor, and
   * that anchor's `aria-controls` back to the panel. Panels are matched to tabs
   * by id *suffix* (`overview-tab`, …) because the collapsed copy is mounted
   * with `id-namespace="collapsed-"`, so its anchors carry a `collapsed-`
   * prefix (`collapsed-overview-tab`) while the main bar keeps the canonical
   * `overview-tab` — an exact-equality match would miss the prefixed group.
   * Called with the currently-visible group's registration (its ids only), so
   * the suffix match resolves once and the bidirectional wiring points at the
   * tabs the user is actually on.
   */
  // Note: production browser builds do NOT scope anchor ids; the `idNamespace`
  // prefix is what keeps the two co-mounted groups' ids distinct. See
  // docs/W-23636455-listing-detail-v2-page-layout.md ("Anchor-id namespacing").
  setTabAttributes(registration) {
    const tabIds = registration.tabsIds;
    if (!tabIds) {
      return;
    }

    // v1's `listing-overview`. The v1 More Details panel is split into
    // first-class Specs / Security / Support tabs, plus a Pricing tab.
    const panelToTab = [[this.template.querySelector('listing-overview-v2'), 'overview-tab'], [this.template.querySelector('listing-skill-overview'), 'overview-tab'], [this.template.querySelector('listing-security'), 'security-tab'], [this.template.querySelector('listing-competencies-v2'), 'competencies-tab'], [this.template.querySelector('#specs-panel'), 'specs-tab'], [this.template.querySelector('#plugin-specs-panel'), 'specs-tab'], [this.template.querySelector('listing-reviews-v2'), 'reviews-tab'], [this.template.querySelector('#pricing-panel'), 'pricing-tab'], [this.template.querySelector('listing-more-resources-v2'), 'resources-tab'], [this.template.querySelector('#support-panel'), 'support-tab'], [this.template.querySelector('#packageFiles-panel'), 'packageFiles-tab'], [this.template.querySelector('#found-in-panel'), 'found-in-tab']].map(([panel, tabSuffix]) => [panel, tabIds.find(id => id.includes(tabSuffix))]);
    for (const [panel, tabId] of panelToTab) {
      if (panel && tabId) {
        panel.setAttribute('aria-labelledby', tabId);
        registration.setAriaControls(tabId, panel.getAttribute('id'));
      }
    }
  }

  /**
   * Registration of the per-group callbacks for tab accessibility and focus.
   * Both mounted groups (inline + collapsed-nav) fire this; we store each
   * under its `data-id` so `activeTabRegistration` can pick the visible one.
   */
  // @ts-expect-error * Automated Type Error Fix
  handleTabRegister(event) {
    const groupId = event.target?.getAttribute('data-id') ?? ListingDetailV2.PRIMARY_TAB_GROUP_ID;
    this.tabRegistrations = {
      ...this.tabRegistrations,
      [groupId]: {
        tabsIds: event.detail.tabsIds,
        setAriaControls: event.detail.setAriaControls,
        focusActiveTab: event.detail.focusActiveTab
      }
    };
  }

  /**
   * Mark every tab group except the visible one `inert`, so only the on-screen
   * group is focusable and exposed to assistive tech. `inert` is presence-based
   * (any value, incl. `"false"`, is inert), so we toggle the DOM property
   * imperatively rather than via a template attribute binding. Cheap: two
   * `querySelector`s + a boolean write on each render, and the DOM only mutates
   * when the value actually flips.
   */
  syncTabGroupInertness() {
    const visible = this.visibleTabGroupId;
    for (const groupId of [ListingDetailV2.PRIMARY_TAB_GROUP_ID, ListingDetailV2.COLLAPSED_TAB_GROUP_ID]) {
      const group = this.template?.querySelector(`[data-id="${groupId}"]`);
      if (!group) {
        continue;
      }
      const shouldBeInert = groupId !== visible;
      if (group.inert !== shouldBeInert) {
        group.inert = shouldBeInert;
      }
    }
  }

  /** Re-sync visible tab after async listing load (e.g. deep link to Resources tab). */
  applyTabFromRouterAfterListingLoad() {
    const {
      params
    } = routerStore.getState();
    const tabFromUrl = params?.tab || OVERVIEW_TAB_CODE;
    this.currentTab = tabFromUrl;
    this.showActiveTabPanel(tabFromUrl);
  }

  /**
   * Redirects to the Overview tab by setting the active panel and updating the URL
   */
  redirectToOverview() {
    this.currentTab = OVERVIEW_TAB_CODE;
    this.showOverview = true;
    // Fallback-to-Overview path also lands the provider band, so fetch here too
    // (deduped in the store by listing id — see fetchMoreFromProvider).
    this.fetchMoreFromProvider();
    navigate(`/${Routes.LISTING}?${upsertQueryParams(['tab', OVERVIEW_TAB_CODE])}`, {
      historyAPIMethod: 'replaceState'
    });
  }

  // @ts-expect-error * Automated Type Error Fix
  showActiveTabPanel(tabCode) {
    this.showOverview = false;
    this.showReviews = false;
    this.showSpecs = false;
    this.showSecurity = false;
    this.showSupport = false;
    this.showPricing = false;
    this.showResources = false;
    this.showCompetencies = false;
    this.showPackageFiles = false;
    this.showFoundIn = false;

    // During an uncached load the listing-dependent tab set is not authoritative yet. Keep the
    // URL untouched and render the Overview shell temporarily; setOrFetchListing re-applies the
    // requested tab after the listing arrives. Without this hold, a valid deep link is rewritten
    // to Overview before the page knows whether Files, Found In, Resources, etc. are available.
    if (this.isListingLoading) {
      this.showOverview = true;
      return;
    }

    // Once loading finishes, the rendered tab collection is the single source of truth for
    // deep-link validity. This prevents hidden panels (for example skill Specs or plugin
    // Security) from activating with no matching tab/ARIA registration and avoids their
    // irrelevant fetches.
    if (!this.tabs.some(tab => tab.code === tabCode)) {
      this.redirectToOverview();
      return;
    }
    switch (tabCode) {
      case OVERVIEW_TAB_CODE:
        this.showOverview = true;
        // Overview is the only tab whose "More from {provider}" band reads
        // this data; fetch on activation rather than on every listing load.
        this.fetchMoreFromProvider();
        break;
      case COMPETENCIES_TAB_CODE:
        if (this.isConsultantListing) {
          this.showCompetencies = true;
        } else {
          // If Competencies tab is not available, fall back to Overview
          this.redirectToOverview();
        }
        break;
      case SPECS_TAB_CODE:
        if (!this.isConsultantListing) {
          this.showSpecs = true;
          // Lazily code-split the Specs panel (see specsPanelCtor) on first activation.
          // Only the non-plugin `<listing-specs>` panel is bound via specsPanelCtor;
          // plugin listings render the statically-bundled `<listing-plugin-specs>`
          // instead (showForceSpecs is false for them), so skip the chunk download.
          if (!this.isPluginListing) {
            this.loadSpecsPanel();
          }
          // Specs-only data: fetch here (tab activation) rather than on every listing load.
          this.fetchPrerequisiteListings();
        } else {
          this.redirectToOverview();
        }
        break;
      case SECURITY_TAB_CODE:
        if (!this.isConsultantListing) {
          this.showSecurity = true;
          // Lazily code-split the Security panel (see securityPanelCtor) on first activation.
          this.loadSecurityPanel();
        } else {
          this.redirectToOverview();
        }
        break;
      case SUPPORT_TAB_CODE:
        this.showSupport = true;
        break;
      case PACKAGE_FILES_TAB_CODE:
        if (this.isSkillListing || this.isPluginListing) {
          this.showPackageFiles = true;
        } else {
          this.redirectToOverview();
        }
        break;
      case FOUND_IN_TAB_CODE:
        if (this.isSkillListing && this.hasParentPlugins) {
          this.showFoundIn = true;
        } else {
          this.redirectToOverview();
        }
        break;
      case REVIEWS_TAB_CODE:
        // Skills/plugins have no reviews feature; fall back to Overview.
        if (this.isSkillListing || this.isPluginListing) {
          this.redirectToOverview();
        } else {
          this.showReviews = true;
          // Lazily code-split the Reviews panel (see reviewsPanelCtor) on first activation.
          this.loadReviewsPanel();
        }
        break;
      case PRICING_TAB_CODE:
        if (this.showPricingTab) {
          this.showPricing = true;
        } else {
          // Listings without a Pricing tab (consultant, skill/plugin, or
          // strictly-free) redirect to Overview if deep-linked to ?tab=p.
          this.redirectToOverview();
        }
        break;
      case RESOURCES_TAB_CODE:
        if (this.hasListingResources) {
          this.showResources = true;
        } else if (this.isListingLoading) {
          this.showOverview = true;
        } else {
          this.redirectToOverview();
        }
        break;
      default:
        this.currentTab = OVERVIEW_TAB_CODE;
        this.showOverview = true;
    }
  }

  /**
   * Lazy-load the three heaviest tab panels the first time each tab activates,
   * caching the resolved constructor so a re-activation is a no-op. Bound via
   * `lwc:dynamic` in the template, so the element mounts once both its `if:true`
   * gate is set and the constructor resolves. Keeping these out of the initial
   * `listingDetailV2` chunk is the point (see the *PanelCtor fields); the panels'
   * own loading state covers the one-time first-click fetch. Errors are swallowed
   * to a warning — a failed split leaves the tab empty rather than throwing, and
   * a later activation retries.
   */
  loadReviewsPanel() {
    if (this.reviewsPanelCtor) {
      return;
    }
    import('listing/reviewsV2').then(module => {
      this.reviewsPanelCtor = module.default;
    }).catch(error => handleException(error, 'listingDetailV2.loadReviewsPanel', {
      level: 'warn'
    }));
  }
  loadSpecsPanel() {
    if (this.specsPanelCtor) {
      return;
    }
    import('listing/specs').then(module => {
      this.specsPanelCtor = module.default;
    }).catch(error => handleException(error, 'listingDetailV2.loadSpecsPanel', {
      level: 'warn'
    }));
  }
  loadSecurityPanel() {
    if (this.securityPanelCtor) {
      return;
    }
    import('listing/security').then(module => {
      this.securityPanelCtor = module.default;
    }).catch(error => handleException(error, 'listingDetailV2.loadSecurityPanel', {
      level: 'warn'
    }));
  }
  get logo() {
    return getListingLogo(this.listing);
  }
  get extensionListing() {
    return selectExtensionListing(this.listing);
  }
  get pricingPlan() {
    // @ts-expect-error * Automated Type Error Fix
    return this.listing?.pricing;
  }

  /**
   * Award-deck cards for the provider's achievements; empty when it has none.
   * Partner of the Year is included when `FF_TRYBUY_POTY_BADGE` is on; consultant
   * achievements (Summit tier / FDE partner) remain ungated here. Unlike v1,
   * nothing is gated behind `FF_TRYBUY_FDE_BADGE`: v2 already sits behind its own
   * feature flag and leaves the deck ungated for consultant listings.
   */
  get providerAchievementCards() {
    return selectProviderAchievementCards(this.listing, FeatureFlags.FF_TRYBUY_POTY_BADGE);
  }
  get shouldShowAwardDeck() {
    return this.providerAchievementCards.length > 0;
  }
  get hasListingResources() {
    // @ts-expect-error * Automated Type Error Fix
    const content = this.listing?.['listing/plugins/Content'];
    return Array.isArray(content) && content.length > 0;
  }
  get listingResources() {
    // @ts-expect-error * Automated Type Error Fix
    const content = this.listing?.['listing/plugins/Content'];
    return Array.isArray(content) ? content : [];
  }

  // TODO - TECH_DEBT: This isn't a concern of the listing detail page, it shouldn't be here
  get reviewSummary() {
    return {
      // @ts-expect-error * Automated Type Error Fix
      listingId: this.listing?.oafId,
      // @ts-expect-error * Automated Type Error Fix
      reviewCount: this.listing?.reviewsSummary?.reviewCount,
      // @ts-expect-error * Automated Type Error Fix
      averageRating: this.listing?.reviewsSummary?.averageRating
    };
  }
  get isPrivateListing() {
    return !this.listing.public;
  }
  get privateBannerMessage() {
    return this.i18n?.listingDetails?.privateBanner?.message ?? '';
  }
  handleInstallStateChange(event) {
    this.marketplaceInstallationTab = event.detail.installationTab;
    this.marketplaceInstallationMethodId = event.detail.installationMethodId;
  }

  // Enrich only explicitly marked AI Asset actions plus first-class page tabs. Related Skills
  // listing tiles and Copy Install Prompt deliberately remain on their established generic paths.
  // @ts-expect-error * Analytics detail is progressively enriched by nested components.
  handleAnalytics(event) {
    event.detail.currentListingId = this.currentListingId;
    if (!this.isSkillListing && !this.isPluginListing) {
      return;
    }
    let context = event.detail.aiAssetAnalytics;
    if (!context && event.detail.elementType === 'tab') {
      const callToAction = event.detail.link?.callToAction;
      const canonicalTabLabels = {
        'overview-tab': 'Overview',
        'found-in-tab': 'Found In',
        'packageFiles-tab': 'Files',
        'specs-tab': 'Specs',
        'support-tab': 'Support'
      };
      const clickedTabLabel = canonicalTabLabels[callToAction?.name] ?? callToAction?.label;
      context = aiAssetEngagement({
        clickText: clickedTabLabel,
        clickUrl: callToAction?.url,
        elementTitle: GtmListingDetailClickType.getItemTitle(event.detail),
        elementType: 'tab'
      });
      event.detail.aiAssetAnalytics = context;
    }
    if (!context) {
      return;
    }
    context.aiAssetKind = this.isSkillListing ? 'skill' : 'plugin';
    if (context.kind === 'engagement') {
      if (getExternalInstallConfig(this.listing).isExternal) {
        // External listings have no native tabs or methods. Derive this from the current
        // listing so fresh page-tab events and SPA navigations cannot inherit the default
        // or a stale native install state.
        const hasExternalAction = this.isPluginListing && this.listing?.external_id === CLAUDEFORCE_LISTING_ID && !!getExternalInstallConfig(this.listing).installUrl;
        if (hasExternalAction) {
          context.installationTab = 'External';
        } else {
          delete context.installationTab;
        }
        delete context.installationMethodId;
      } else if (this.isSkillListing && getSkillInstallOptions(this.listing, this.marketplaceInstallationMethodId ?? 'claude-code').length > 0 || this.isPluginListing && getClaudePluginInstallCommands(this.listing) && getSkillSupportedHarnesses(this.listing).some(harness => harness.installMethodId === 'claude-code' && (harness.hasPrompt || harness.hasCli))) {
        context.installationTab ??= this.marketplaceInstallationTab;
        context.installationMethodId ??= this.marketplaceInstallationMethodId;
      } else {
        // Cards without an active install action have no install attribution.
        delete context.installationTab;
        delete context.installationMethodId;
      }
    }
  }
  get displayChat() {
    return this.userToken && this.listing?.chat?.token && this.listing?.chat?.active;
  }
  get chatToken() {
    // @ts-expect-error * Automated Type Error Fix
    return this.listing?.chat?.token;
  }
  get searchBlurb() {
    return this.extensionListing?.searchBlurb || '';
  }
  get hasSearchBlurb() {
    return Boolean(this.searchBlurb);
  }
  get isSupportedListing() {
    return !isSlackListing(this.listing) && isSupportedTechnologyType(this.listing);
  }
  get carouselItems() {
    if (!this.listing) {
      return [];
    }
    // @ts-expect-error * Automated Type Error Fix
    const carouselItems = this.listing['listing/plugins/Carousel']?.items || [];
    return carouselItems.map(this.formatCarouselItem);
  }
  get demoItems() {
    if (!this.listing) {
      return [];
    }
    const listingWithDemo = this.listing;
    return listingWithDemo['listing/plugins/Demo']?.items || [];
  }
  get hasDemoItem() {
    return this.demoItems.length > 0;
  }
  get watchDemoUrl() {
    const demoItem = this.demoItems[0];
    if (!demoItem) {
      return '';
    }
    return this.hasDemoLeadsEnabled ? this.generateCtaUrlLegacySite('demo') : demoItem.url || this.generateCtaUrlLegacySite('demo');
  }

  /**
   * The partner's Demo URL, kept separate from `watchDemoUrl` so the banner can
   * still show a video preview for lead-enabled demos, where navigation goes to
   * the legacy lead page rather than the video itself.
   * Empty (not a legacy CTA fallback) when the Demo item has no URL — `watchDemo`
   * already falls back to `href` for the preview in that case.
   */
  get watchDemoPreviewUrl() {
    return this.demoItems[0]?.url || '';
  }
  get hasDemoLeadsEnabled() {
    return selectLeadTrialInformation(this.listing)?.enableDemoLeads ?? false;
  }
  get hasPrivateOfferRequestLeadsEnabled() {
    return selectLeadTrialInformation(this.listing)?.enableOfferRequestLeads ?? false;
  }
  get isFavoriteButtonDisabled() {
    return this.savedListingsLoadingStatus === SavedListingsLoadingStatus.LOADING;
  }
  get shouldShowFavoriteButton() {
    return !this.isSkillListing && !this.isPluginListing;
  }
  get favoriteButtonClassName() {
    let className = 'favorite-button';
    // Adds 'disabled' class to avoid undesired hover behaviors on 'ax-iteractive-icon'
    if (this.isFavoriteButtonDisabled) {
      className += ' disabled';
    }
    return className;
  }
  get favoriteButtonIcon() {
    // v2 (Figma 7764:134838): un-favorited heart is a solid neutral-grey heart
    // (Icon/Light #AEAEAE). It uses `favorite-heart-filled-neutral` — the same
    // path as the favorited `favorite-heart-filled` sprite, but with no
    // hardcoded fill, so it honors the grey `--axds-ovr-icon-interactive-icon-
    // primary-hue` set on `.favorite-button`. (The design-system
    // `favorite-heart-filled` sprite bakes a red `fill="#BA0517"` we can't
    // recolor, so we ship this recolorable twin for the neutral state.) Both
    // share the same 32×32 viewBox and path, so the glyph is pixel-identical
    // across the toggle — only the color changes (grey → red).
    return this.listingInUserFavorites ? 'favorite-heart-filled' : 'favorite-heart-filled-neutral';
  }
  get favoriteButtonIconDescription() {
    return this.listingInUserFavorites ? this.i18nModule.listingDetails.getItNowButtons.removeFavorite() : this.i18nModule.listingDetails.getItNowButtons.addFavorite();
  }
  get favoriteButtonAnalyticsName() {
    return this.listingInUserFavorites ? 'remove from favorites' : 'add to favorites';
  }
  get showOfferRequestSuccessNotification() {
    return !!this.offerRequestSuccessMessage;
  }
  /*LWC compiler v7.1.3*/
}
/** `data-id` of the tab group whose ids the panel wiring is keyed against. */
ListingDetailV2.PRIMARY_TAB_GROUP_ID = 'tab-group';
ListingDetailV2.COLLAPSED_TAB_GROUP_ID = 'collapsed-tab-group';
_registerDecorators(ListingDetailV2, {
  track: {
    isSummaryCollapsed: 1,
    hasCollapsedOnce: 1
  },
  wire: {
    deviceType: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: viewportStore,
          selector: selectDeviceType
        };
      }
    },
    routerStoreWire: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: selectMatch,
          equalityFn
        };
      }
    },
    userStoreWire: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: userStore,
          selector: selectUserFavorite,
          equalityFn
        };
      }
    },
    i18nStoreWire: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: i18nStore,
          selector: selectI18n,
          equalityFn
        };
      }
    },
    providerState: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: moreFromProviderStore
        };
      }
    }
  },
  fields: ["i18n", "i18nModule", "firstRendered", "userAuthenticated", "isLoading", "userToken", "userId", "savedListings", "savedListingsLoadingStatus", "isListingLoading", "listing", "publisher", "currentListingId", "currentListingRouteUrl", "listingRequestGeneration", "prerequisiteListing", "showOverview", "showReviews", "showSpecs", "showSecurity", "showSupport", "showPricing", "showResources", "showCompetencies", "showPackageFiles", "showFoundIn", "tabRegistrations", "lastFocusedTab", "pendingInlineTabFocusAfterExpand", "currentTab", "offerRequestSuccessMessage", "claudeforceBetaSignupSucceeded", "headerScrollHandler", "headerResizeHandler", "scrollFrameId", "reviewsPanelCtor", "specsPanelCtor", "securityPanelCtor", "maxObservedHeaderHeightPx", "formatCarouselItem"]
});
const __lwc_component_class_internal = _registerComponent(ListingDetailV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;